import { Button } from '@freelancer-os/ui/src/components/ui/button';
import { Check, Pencil, Send as SendIcon } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../hooks/useChat';
import { useFileUpload } from '../../hooks/useFileUpload';
import { useChatStore } from '../../stores/chatStore';
import { ArtifactPanel } from './components/ArtifactPanel';
import { ChatHeader } from './components/ChatHeader';
import { ChatInput, type ChatInputHandle } from './components/ChatInput';
import { ChatMessages } from './components/ChatMessages';
import { ThreadSidebar } from './components/ThreadSidebar';
import { useRef, useState, type DragEvent } from 'react';

export function ChatPage() {
  const { t } = useTranslation('chat');
  const { messages, status, isStreaming, artifacts, sendMessage } = useChat();
  const setPendingHydration = useChatStore((s) => s.setPendingHydration);
  const threadId = useChatStore((s) => s.threadId);
  const navigate = useNavigate();
  const [artifactVisible, setArtifactVisible] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showFileHint, setShowFileHint] = useState(false);
  const chatInputRef = useRef<ChatInputHandle>(null);

  const { files, addFiles, removeFile, clear, isUploading, pendingFiles } = useFileUpload({
    linkedThreadId: threadId ?? null,
  });

  const pickSuggestion = (text: string, attachLabel?: string) => {
    setInputValue(text);
    setShowFileHint(Boolean(attachLabel));
    // Defer focus until after the controlled value re-renders the textarea.
    requestAnimationFrame(() => chatInputRef.current?.focus());
  };

  const handleInputChange = (next: string) => {
    setInputValue(next);
    // Clearing the prompt also dismisses the file hint — they belong to the
    // same suggestion-pick gesture.
    if (next.trim() === '') setShowFileHint(false);
  };

  // Hide the hint once the user has actually attached a file.
  const effectiveFileHint = showFileHint && files.length === 0;

  const handleEditManual = (artifact?: { data: { invoiceDraft: unknown } } | null) => {
    const sourceArtifact = artifact ?? artifacts[artifacts.length - 1] ?? null;
    if (sourceArtifact) {
      setPendingHydration(sourceArtifact.data as Parameters<typeof setPendingHydration>[0]);
    }
    navigate('/invoices/new');
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true);
  };
  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear when leaving the outer container (not when crossing child boundaries)
    if (e.currentTarget === e.target) setIsDragging(false);
  };
  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  return (
    <div className="flex h-full">
      <ThreadSidebar />
      {/* Main Chat Area — drag-drop covers whole area (header + messages + input) */}
      <div
        className="flex-1 flex flex-col min-w-0 bg-background relative"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isDragging && (
          <div className="absolute inset-0 bg-brand-500/10 border-2 border-dashed border-brand-500 flex items-center justify-center z-50 pointer-events-none">
            <span className="text-base font-medium text-brand-700">
              {t('input.dropFiles', { defaultValue: 'Thả file để tải lên' })}
            </span>
          </div>
        )}
        <ChatHeader
          title="Hoá đơn website anh Tùng"
          artifactVisible={artifactVisible}
          onToggleArtifact={() => setArtifactVisible((v) => !v)}
        />
        <ChatMessages
          messages={messages}
          status={status}
          onSuggestionPick={pickSuggestion}
        />

        {/* Quick Action Chips */}
        <div className="px-5 shrink-0">
          <div className="max-w-[720px] mx-auto flex flex-wrap gap-2 pb-3">
            <Button
              variant="outline"
              className="gap-1.5 px-3.5 py-2 h-auto text-[13px] font-medium text-surface-600 hover:border-brand-400 hover:text-brand-600 hover:bg-brand-50/40"
              onClick={() => handleEditManual()}
            >
              <Pencil className="w-3.5 h-3.5" strokeWidth={2} />
              {t('actions.editManual')}
            </Button>
            <Button
              className="gap-1.5 px-3.5 py-2 h-auto border-2 border-brand-500 bg-brand-50 text-[13px] font-semibold text-brand-700 hover:bg-brand-100 shadow-none"
              variant="outline"
              disabled
            >
              <Check className="w-3.5 h-3.5" strokeWidth={2} />
              {t('actions.approveQR')}
            </Button>
            <Button
              variant="outline"
              className="gap-1.5 px-3.5 py-2 h-auto text-[13px] font-medium text-surface-600 hover:border-brand-400 hover:text-brand-600 hover:bg-brand-50/40"
            >
              <SendIcon className="w-3.5 h-3.5" strokeWidth={2} />
              {t('actions.sendZalo')}
            </Button>
          </div>
        </div>

        <ChatInput
          ref={chatInputRef}
          value={inputValue}
          onValueChange={handleInputChange}
          onSend={(message, attachments) => {
            sendMessage(message, attachments);
            setShowFileHint(false);
          }}
          disabled={isStreaming}
          files={files}
          pendingFiles={pendingFiles}
          isUploading={isUploading}
          addFiles={addFiles}
          removeFile={removeFile}
          clear={clear}
          showFileHint={effectiveFileHint}
          onDismissFileHint={() => setShowFileHint(false)}
        />
      </div>

      {/* Artifact Panel — keyed by threadId so it remounts (and resets
          selectedIndex) when switching to a different conversation. */}
      {artifactVisible && (
        <ArtifactPanel
          key={threadId ?? 'new'}
          artifacts={artifacts}
          threadId={threadId}
          onClose={() => setArtifactVisible(false)}
          onEditManual={handleEditManual}
        />
      )}
    </div>
  );
}
